import React, { FC } from 'react';
import styled from 'styled-components';
import {
  BROWSER_BREAKPOINTS,
  Job,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useIsMobileDevice,
} from '@flicent/fieldpulse-react-component-library-frontend';

import JobSummaryTableProps from './JobSummaryTable.props';

const DATE_TIME_COLUMN_WIDTH: number = 3.3;
const LOCATION_COLUMN_WIDTH: number = 6;

const StyledRoot = styled.table`
  // Compact table for mobile view
  @media screen and ${ BROWSER_BREAKPOINTS.MOBILE } {
    thead,
    colgroup {
      display: none;
    }

    tr {
      display: grid;
      align-items: flex-start;
      grid-template-columns: 1fr 1fr;
      grid-column-gap: 8px;
    }

    td {
      padding-left: 0;
    }

    td:last-of-type { // Make location cell full width on mobile
      grid-column: 1 / -1;
    }
  }
  // ==============================
`;

const StyledMobileCellLabel = styled.span`
  display: none;
  padding-bottom: 8px;

  &::after {
    content: ':';
  }

  @media screen and ${ BROWSER_BREAKPOINTS.MOBILE } {
    display: block;
  }
`;

const JobSummaryTable: FC<JobSummaryTableProps> = ({ company, jobs }) => {
  const isMobileScreen = useIsMobileDevice();

  const isSummaryVisible: boolean = jobs.some((job) => job.is_job_summary_visible);
  if (!isSummaryVisible) {
    return null;
  }

  const head: string[] = [
    `${ company.job_name } #`,
    `${ company.job_name } Title`,
    'Status',
    'Location',
  ];

  let dateTimeColumnWidth: string;
  let locationColumnWidth: string;

  const isDateVisible: boolean = jobs.some((job) => job.is_job_date_visible);
  const isTimeVisible: boolean = jobs.some((job) => job.is_job_time_visible);
  const isDateOrTimeVisible: boolean = isDateVisible || isTimeVisible;

  if (isDateOrTimeVisible) {
    const isDateAndTimeVisible: boolean = isDateVisible && isTimeVisible;

    let label: string;
    if (isDateAndTimeVisible) {
      label = `Date & Time`;
    } else if (isDateVisible) {
      label = 'Date';
    } else {
      label = 'Time';
    }

    // Insert the "Date/Time" before the "Location" column
    head.splice(3, 0, label);

    dateTimeColumnWidth = `${ DATE_TIME_COLUMN_WIDTH }fr`;
    locationColumnWidth = `${ LOCATION_COLUMN_WIDTH }fr`;
  } else {
    dateTimeColumnWidth = '';
    locationColumnWidth = `${ DATE_TIME_COLUMN_WIDTH + LOCATION_COLUMN_WIDTH }fr`;
  }

  const firstColumnWidth: string = `${ head[0].length + 2 }ch`; // take the exact required width (in fact, almost exact – see "ch" CSS-unit)
  const gridTemplateColumns: string = isMobileScreen ? '1fr 1fr' : `${ firstColumnWidth } 3fr 1.5fr ${ dateTimeColumnWidth } ${ locationColumnWidth }`;

  const headComponent = isMobileScreen ? null : <TableHead>
    <TableRow gridTemplateColumns={ gridTemplateColumns }>
      { head.map((label, index) => (
        <TableCell key={ index } isHead={ true }>{ label }</TableCell>
      )) }
    </TableRow>
  </TableHead>;

  const bodyComponent = (
    <TableBody>
      { jobs.map((job: Job) => {
        const id = job.cuid;
        return (
          <TableRow key={ id } gridTemplateColumns={ gridTemplateColumns }>
            <TableCell>
              <Typography fontWeight="bold" variant="body1" component={ StyledMobileCellLabel }>{ head[0] }</Typography>
              <Link href={ `#${ id }` }>{ job.cuid }</Link>
            </TableCell>
            <TableCell>
              <Typography fontWeight="bold" variant="body1" component={ StyledMobileCellLabel }>{ head[1] }</Typography>
              <Link href={ `#${ id }` }>{ job.title }</Link>
            </TableCell>
            <TableCell>
              <Typography fontWeight="bold" variant="body1" component={ StyledMobileCellLabel }>{ head[2] }</Typography>
              { job.status_name }
            </TableCell>
            { isDateOrTimeVisible && (
              <TableCell>
                <Typography fontWeight="bold" variant="body1"
                            component={ StyledMobileCellLabel }>{ head[3] }</Typography>
                { job.display_time }
              </TableCell>
            ) }
            <TableCell>
              <Typography fontWeight="bold" variant="body1" component={ StyledMobileCellLabel }>{ head[4] }</Typography>
              { job.location }
            </TableCell>
          </TableRow>
        );
      }) }
    </TableBody>
  );

  return (
    <Paper>
      <Table as={ StyledRoot }>
        { headComponent }
        { bodyComponent }
      </Table>
    </Paper>
  );
};


export default JobSummaryTable;